// src/pages/admin/AdminCurriculum.tsx
//
// The lesson library — every week of every track, imported from the
// curriculum lesson plans. Each week opens on its own page
// (/admin/curriculum/:weekKey) with its micro-lessons, scripts, videos,
// weekly target and the full original lesson plan.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useApproveAllLessonsMutation,
  useGetLibraryQuery,
  useGetLibraryWeeksQuery,
} from "../../app/api/libraryApi";
import type { LibraryApproval } from "../../app/api/libraryApi";
import { selectAdminRole } from "../../app/features/adminAuthSlice";
import type { LibraryTrack, WeekSummary } from "../../app/api/libraryApi";
import { EmptyState, PageHeader, Skeleton, trackConfig } from "./AdminShared";

import { BookOpen, Star } from "lucide-react";
const TRACKS: LibraryTrack[] = ["foundation_builders", "explorers", "innovators"];

const TERM_LABEL: Record<string, string> = {
  Q1: "Quarter 1",
  Q2: "Quarter 2",
  Q3: "Quarter 3",
  Stage1: "Stage 1",
  Stage2: "Stage 2",
  Stage3: "Stage 3",
  Stage4: "Stage 4",
};

export const CONVERSION_LABEL: Record<
  WeekSummary["conversionStatus"],
  { label: string; cls: string }
> = {
  not_started: { label: "Lesson plan only", cls: "bg-[#f1f3f8] text-[#8189a0]" },
  draft: { label: "Micro-lessons: draft", cls: "bg-[#fff8ee] text-[#7a4a10]" },
  in_review: { label: "In review", cls: "bg-[#f0f7ff] text-[#1a4a7a]" },
  approved: { label: "Approved", cls: "bg-[#edfaf4] text-[#2a7a56]" },
};

function WeekRow({ w }: { w: WeekSummary }) {
  const conv = CONVERSION_LABEL[w.conversionStatus];
  const lessons = w.microLessons ?? 0;
  const videos = w.videos ?? 0;
  return (
    <Link
      to={`/admin/curriculum/${w.key}`}
      className="flex items-center gap-3 border-b border-[#f7f8fb] px-4 py-3 transition last:border-0 hover:bg-[#f9fafc]"
    >
      <span className="flex h-7 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#ebeff7] text-[11.5px] font-bold text-[#1b2d5e]">
        W{String(w.weekNumber).padStart(2, "0")}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-[#16213e]">
          {w.isShowcase && <span className="mr-1"><Star className="h-4 w-4" aria-hidden /></span>}
          {w.title}
        </p>
        {w.topics && (
          <p className="mt-0.5 truncate text-[11.5px] text-[#8189a0]">{w.topics}</p>
        )}
      </div>
      <div className="hidden flex-shrink-0 items-center gap-1.5 sm:flex">
        {w.hybridOnsite && (
          <span
            title="In hybrid, this week happens on-site"
            className="rounded-full bg-[#fff4f1] px-2 py-0.5 text-[11px] font-bold text-[#7a3020]"
          >
            On-site day
          </span>
        )}
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${conv.cls}`}>
          {conv.label}
        </span>
      </div>
      <span className="w-20 flex-shrink-0 text-right text-[11.5px] font-semibold text-[#5a6278]">
        {lessons > 0 ? (
          <>
            {lessons} lessons
            <br />
            <span className={videos === lessons ? "text-[#2a7a56]" : "text-[#a6adbf]"}>
              {videos}/{lessons} videos
            </span>
          </>
        ) : (
          <span className="text-[#a6adbf]">—</span>
        )}
      </span>
    </Link>
  );
}

function TrackLibrary({ track }: { track: LibraryTrack }) {
  const { data, isLoading } = useGetLibraryWeeksQuery(track);
  const weeks = useMemo(() => data?.data ?? [], [data]);
  const terms = useMemo(
    () => Array.from(new Set(weeks.map((w) => w.term))),
    [weeks],
  );
  const [term, setTerm] = useState<string | null>(null);
  const activeTerm = term && terms.includes(term) ? term : terms[0];

  if (isLoading)
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded-[16px]" />
        ))}
      </div>
    );

  if (!weeks.length)
    return (
      <EmptyState
        icon={BookOpen}
        title="No lessons imported yet"
        subtitle="Run scripts/importCurriculum.js on the server to load the lesson plans."
      />
    );

  const shown = weeks.filter((w) => w.term === activeTerm);
  const lessons = shown.reduce((n, w) => n + (w.microLessons ?? 0), 0);
  const videos = shown.reduce((n, w) => n + (w.videos ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {terms.map((t) => (
          <button
            key={t}
            onClick={() => setTerm(t)}
            className={[
              "rounded-xl px-3 py-1.5 text-[12.5px] font-bold transition",
              t === activeTerm
                ? "bg-[#1b2d5e] text-white"
                : "bg-white border border-[#e5e8ef] text-[#8189a0] hover:text-[#16213e]",
            ].join(" ")}
          >
            {TERM_LABEL[t] ?? t}
          </button>
        ))}
      </div>

      <p className="text-[12.5px] text-[#8189a0]">
        {shown.length} weeks · {lessons} micro-lessons · {videos} with video
      </p>

      <div className="overflow-hidden rounded-[18px] border border-[#e5e8ef] bg-white">
        {shown.map((w) => (
          <WeekRow key={w.key} w={w} />
        ))}
      </div>
    </div>
  );
}

/** How many lessons children can see, and one button to approve the rest. */
function ApprovalCard({ approval }: { approval: LibraryApproval }) {
  const role = useSelector(selectAdminRole);
  const canApprove = role === "admin" || role === "super_admin";
  const [approveAll, { isLoading }] = useApproveAllLessonsMutation();
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const drafts = approval.lessons - approval.approvedLessons;
  const pct = approval.lessons ? Math.round((approval.approvedLessons / approval.lessons) * 100) : 0;

  const run = async () => {
    setNote(null);
    try {
      const res = await approveAll().unwrap();
      setNote({ ok: true, text: `${res.message} Children can now see them.` });
    } catch (e) {
      setNote({ ok: false, text: (e as { data?: { error?: string } })?.data?.error ?? "Could not approve the lessons." });
    }
    setConfirming(false);
  };

  return (
    <div className="mb-6 rounded-[22px] border border-[#e5e8ef] bg-white p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-[#16213e]">
            {approval.approvedLessons} of {approval.lessons} micro-lessons approved
            <span className="font-normal text-[#5f6780]">
              {" "}· {approval.approvedWeeks} of {approval.weeks} weeks
            </span>
          </p>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#ebeff7]">
            <div className="h-full rounded-full bg-[#2a7a56]" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-[#5f6780]">
            Children only see approved lessons. {drafts > 0 ? `${drafts} are still drafts.` : "Every lesson is approved."}
          </p>
        </div>
        {canApprove && drafts > 0 && (
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            {confirming ? (
              <>
                <span className="text-xs font-semibold text-[#7a4a10]">Approve all {drafts} for children?</span>
                <button
                  type="button"
                  onClick={run}
                  disabled={isLoading}
                  className="rounded-xl bg-[#2a7a56] px-4 py-2 text-xs font-semibold text-white hover:bg-[#23684a] disabled:opacity-50"
                >
                  {isLoading ? "Approving…" : "Yes, approve all"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="rounded-xl px-3 py-2 text-xs font-semibold text-[#5f6780] hover:bg-[#f1f3f8]"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="rounded-xl bg-[#1b2d5e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#24397a]"
              >
                Approve all lessons
              </button>
            )}
          </div>
        )}
      </div>
      {note && <p className={`mt-3 text-xs font-semibold ${note.ok ? "text-[#2a7a56]" : "text-[#b94a3a]"}`}>{note.text}</p>}
    </div>
  );
}

export default function AdminCurriculum() {
  const { data } = useGetLibraryQuery();
  const [activeTrack, setActiveTrack] = useState<LibraryTrack>("foundation_builders");
  const storageConfigured = data?.data.storageConfigured;

  return (
    <>
      <PageHeader
        title="Lesson library"
        subtitle="Every week of the curriculum: weekly targets, micro-lessons, scripts and lesson videos. Open a week to change a video or read its full script."
        action={
          storageConfigured === false ? (
            <span className="rounded-full border border-[#f5d9a0] bg-[#fff8ee] px-3 py-1.5 text-[12.5px] font-semibold text-[#7a4a10]">
              Video storage not set up yet
            </span>
          ) : null
        }
      />

      {data?.data.approval && <ApprovalCard approval={data.data.approval} />}

      <div className="mb-6 flex gap-1 rounded-2xl border border-[#e5e8ef] bg-white p-1.5">
        {TRACKS.map((t) => {
          const tc = trackConfig(t);
          return (
            <button
              key={t}
              onClick={() => setActiveTrack(t)}
              className={[
                "flex flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-4 py-3 text-center transition-all",
                activeTrack === t
                  ? "bg-[#1b2d5e] text-white shadow-[0_4px_12px_rgba(27,45,94,0.30)]"
                  : "text-[#8189a0] hover:bg-[#f1f3f8] hover:text-[#16213e]",
              ].join(" ")}
            >
              <span className="text-xs font-bold">{tc.label}</span>
            </button>
          );
        })}
      </div>

      <TrackLibrary key={activeTrack} track={activeTrack} />
    </>
  );
}
